"use client";

import React, { useState, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Search,
  Sparkles,
  Download,
  Eye,
  Users,
  ArrowRight,
  Star,
  TrendingUp,
  Clock,
  LayoutTemplate,
  Zap,
  ArrowUpRight,
  Flame,
  Boxes,
} from "lucide-react";
import { TEMPLATE_CATEGORIES } from "@/lib/marketplace/types";

interface TemplateCard {
  id: string;
  slug: string;
  name: string;
  shortDescription: string | null;
  category: string;
  tags: string[];
  thumbnailUrl: string | null;
  provider: string | null;
  modelName: string | null;
  installCount: number;
  viewCount: number;
  isFeatured: boolean;
  createdAt: string;
  author: { id: string; name: string | null; image: string | null };
}

interface MarketplaceProps {
  featuredTemplates: TemplateCard[];
  recentTemplates: TemplateCard[];
  categoryCounts: Record<string, number>;
  totalTemplates: number;
  totalInstalls: number;
  totalAuthors: number;
}

function useTemplateSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TemplateCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [sort, setSort] = useState<"popular" | "newest" | "featured">("popular");

  // In-memory cache to avoid re-fetching identical queries
  const searchCache = useRef<Map<string, TemplateCard[]>>(new Map());

  const search = useCallback(
    async (q: string, category: string | null, s: "popular" | "newest" | "featured") => {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      if (category) params.set("category", category);
      params.set("sort", s);
      params.set("limit", "24");
      const cacheKey = params.toString();

      // Return cached results instantly if available
      const cached = searchCache.current.get(cacheKey);
      if (cached) {
        setResults(cached);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`/api/templates/browse?${cacheKey}`);
        if (res.ok) {
          const data = await res.json();
          searchCache.current.set(cacheKey, data.templates);
          setResults(data.templates);
        }
      } catch (e) {
        console.error("Search error:", e);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { query, setQuery, results, loading, activeCategory, setActiveCategory, sort, setSort, search };
}

/* ─── Animated counter ─── */
function AnimatedNumber({ value }: { value: number }) {
  const [display, setDisplay] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const duration = 1200;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(ease * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [value]);
  return <>{display.toLocaleString()}</>;
}

export function TemplateMarketplace({
  featuredTemplates,
  recentTemplates,
  categoryCounts,
  totalTemplates,
  totalInstalls,
  totalAuthors,
}: MarketplaceProps) {
  const { query, setQuery, results, loading, activeCategory, setActiveCategory, sort, setSort, search } =
    useTemplateSearch();
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  const handleSearch = (value: string) => {
    setQuery(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    if (value.length > 0 || activeCategory) {
      setIsSearching(true);
      const t = setTimeout(() => search(value, activeCategory, sort), 300);
      setSearchTimeout(t);
    } else {
      setIsSearching(false);
    }
  };

  const handleCategoryClick = (cat: string | null) => {
    setActiveCategory(cat);
    if (cat === null && !query) {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    search(query, cat, sort);
  };

  const handleSortChange = (s: "popular" | "newest" | "featured") => {
    setSort(s);
    if (isSearching || activeCategory || query) {
      search(query, activeCategory, s);
    }
  };

  const displayTemplates = isSearching ? results : null;

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-[#09090b]">
      {/* ══════ HERO ══════ */}
      <section className="relative isolate overflow-hidden pb-16 pt-24 md:pb-24 md:pt-32">
        {/* Mesh gradient bg */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -left-40 -top-40 h-[500px] w-[500px] rounded-full bg-violet-400/20 blur-[120px] dark:bg-violet-600/10" />
          <div className="absolute -right-32 top-20 h-[400px] w-[400px] rounded-full bg-blue-400/15 blur-[100px] dark:bg-blue-600/10" />
          <div className="absolute bottom-0 left-1/3 h-[350px] w-[350px] rounded-full bg-fuchsia-400/10 blur-[100px] dark:bg-fuchsia-600/5" />
        </div>
        {/* Dot grid */}
        <div className="pointer-events-none absolute inset-0 -z-10 [background-image:radial-gradient(circle,rgba(0,0,0,0.04)_1px,transparent_1px)] [background-size:24px_24px] dark:[background-image:radial-gradient(circle,rgba(255,255,255,0.03)_1px,transparent_1px)]" />

        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            {/* Badge */}
            <div className="group mb-6 inline-flex items-center gap-2 rounded-full border border-violet-200/60 bg-white/70 px-4 py-1.5 text-[13px] font-medium text-violet-700 shadow-sm backdrop-blur-md transition-all hover:border-violet-300 hover:shadow-md dark:border-violet-700/40 dark:bg-white/5 dark:text-violet-300">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-500 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-500" />
              </span>
              Community-Built Templates
              <ArrowUpRight className="h-3 w-3 opacity-50 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
            </div>

            <h1 className="mb-5 text-[2.75rem] font-extrabold leading-[1.1] tracking-tight text-gray-950 dark:text-white md:text-6xl lg:text-7xl">
              Template{" "}
              <span className="bg-gradient-to-r from-violet-600 via-blue-600 to-violet-600 bg-[length:200%_auto] bg-clip-text text-transparent">
                Marketplace
              </span>
            </h1>

            <p className="mx-auto mb-10 max-w-lg text-[17px] leading-relaxed text-gray-500 dark:text-gray-400">
              Discover ready-made chatbot configurations crafted by the community.
              Install in one click, customize, and ship.
            </p>

            {/* Search */}
            <div className="mx-auto max-w-xl">
              <div
                className={`group relative rounded-2xl transition-all duration-300 ${
                  searchFocused
                    ? "shadow-[0_0_0_3px_rgba(139,92,246,0.15),0_20px_60px_-15px_rgba(139,92,246,0.12)]"
                    : "shadow-[0_4px_24px_-4px_rgba(0,0,0,0.06)]"
                }`}
              >
                <Search
                  className={`absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 transition-colors duration-200 ${
                    searchFocused ? "text-violet-500" : "text-gray-400"
                  }`}
                />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder="Search templates..."
                  className="w-full rounded-2xl border border-gray-200/80 bg-white/90 py-4 pl-11 pr-4 text-[15px] backdrop-blur-xl outline-none transition-all placeholder:text-gray-400 dark:border-gray-700/60 dark:bg-gray-900/80 dark:placeholder:text-gray-500"
                />
                {loading && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-500" />
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="mt-10 flex items-center justify-center gap-4 md:gap-6">
              {[
                { icon: Boxes, label: "templates", value: totalTemplates },
                { icon: Download, label: "installs", value: totalInstalls },
                { icon: Users, label: "creators", value: totalAuthors },
              ].map((s) => (
                <div
                  key={s.label}
                  className="flex items-center gap-2.5 rounded-xl border border-gray-200/60 bg-white/60 px-4 py-2.5 backdrop-blur-md dark:border-gray-700/40 dark:bg-white/[0.04]"
                >
                  <s.icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                    <AnimatedNumber value={s.value} />
                  </span>
                  <span className="text-xs text-gray-500">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════ CATEGORIES ══════ */}
      <section className="sticky top-0 z-30 border-b border-gray-200/80 bg-white/80 backdrop-blur-xl dark:border-gray-800/60 dark:bg-[#09090b]/80">
        <div className="container mx-auto px-4">
          <div className="scrollbar-hide -mb-px flex gap-1 overflow-x-auto py-2.5">
            <button
              onClick={() => handleCategoryClick(null)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                !activeCategory
                  ? "bg-gray-900 text-white shadow-sm dark:bg-white dark:text-gray-900"
                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              }`}
            >
              <Sparkles className="h-3 w-3" />
              All
            </button>
            {TEMPLATE_CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleCategoryClick(cat.value)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                  activeCategory === cat.value
                    ? "bg-gray-900 text-white shadow-sm dark:bg-white dark:text-gray-900"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                }`}
              >
                <span className="text-sm">{cat.icon}</span>
                {cat.label}
                {categoryCounts[cat.value] ? (
                  <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] ${
                    activeCategory === cat.value
                      ? "bg-white/20 dark:bg-gray-900/30"
                      : "bg-gray-100 dark:bg-gray-800"
                  }`}>
                    {categoryCounts[cat.value]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ CONTENT ══════ */}
      <div className="container mx-auto px-4 py-10 md:py-14">
        {/* Sort Controls */}
        {(isSearching || activeCategory) && (
          <div className="mb-8 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300" />
                  Searching...
                </span>
              ) : (
                <>{displayTemplates?.length || 0} templates found</>
              )}
            </p>
            <div className="flex items-center rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-800 dark:bg-gray-900">
              {(
                [
                  { key: "popular", icon: TrendingUp, label: "Popular" },
                  { key: "newest", icon: Clock, label: "Newest" },
                  { key: "featured", icon: Star, label: "Featured" },
                ] as const
              ).map((s) => (
                <button
                  key={s.key}
                  onClick={() => handleSortChange(s.key)}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                    sort === s.key
                      ? "bg-gray-900 text-white shadow-sm dark:bg-white dark:text-gray-900"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }`}
                >
                  <s.icon className="h-3 w-3" />
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {isSearching && displayTemplates && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {displayTemplates.map((t, i) => (
              <TemplateCardComponent key={t.id} template={t} index={i} />
            ))}
            {displayTemplates.length === 0 && !loading && (
              <div className="col-span-full flex flex-col items-center py-24">
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-800">
                  <Search className="h-7 w-7 text-gray-400" />
                </div>
                <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">No templates found</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        )}

        {/* Default View */}
        {!isSearching && (
          <>
            {/* Featured */}
            {featuredTemplates.length > 0 && (
              <section className="mb-16">
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 shadow-md shadow-orange-500/20">
                    <Flame className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Featured</h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-800" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {featuredTemplates.map((t, i) => (
                    <TemplateCardComponent key={t.id} template={t} featured index={i} />
                  ))}
                </div>
              </section>
            )}

            {/* Recent */}
            <section className="mb-16">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-400 to-cyan-500 shadow-md shadow-blue-500/20">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Recently Published</h2>
                  <div className="hidden h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-800 sm:block" />
                </div>
                <button
                  onClick={() => {
                    setIsSearching(true);
                    setSort("newest");
                    search("", null, "newest");
                  }}
                  className="group flex items-center gap-1.5 text-[13px] font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  View all
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {recentTemplates.map((t, i) => (
                  <TemplateCardComponent key={t.id} template={t} index={i} />
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="relative isolate overflow-hidden rounded-3xl border border-gray-200/60 bg-white dark:border-gray-800/60 dark:bg-gray-900">
              <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-violet-400/10 blur-[80px] dark:bg-violet-600/10" />
                <div className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-blue-400/10 blur-[80px] dark:bg-blue-600/10" />
                <div className="absolute inset-0 [background-image:radial-gradient(circle,rgba(0,0,0,0.015)_1px,transparent_1px)] [background-size:20px_20px] dark:[background-image:radial-gradient(circle,rgba(255,255,255,0.02)_1px,transparent_1px)]" />
              </div>
              <div className="relative px-8 py-12 text-center md:px-16 md:py-16">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-200/60 bg-violet-50/80 px-3.5 py-1 text-[13px] font-medium text-violet-600 dark:border-violet-700/40 dark:bg-violet-950/50 dark:text-violet-300">
                  <Zap className="h-3 w-3" />
                  Grow with the community
                </div>
                <h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">
                  Share your chatbot as a template
                </h2>
                <p className="mx-auto mb-8 max-w-md text-[15px] text-gray-500 dark:text-gray-400">
                  Publish your chatbot configuration and let others build on your work. Get discovered by thousands of builders.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Link
                    href="/dashboard/templates"
                    prefetch={false}
                    className="group inline-flex items-center gap-2 rounded-xl bg-gray-900 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-gray-900/20 transition-all hover:shadow-gray-900/30 dark:bg-white dark:text-gray-900 dark:shadow-white/10"
                  >
                    Publish a Template
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                  <Link
                    href="/login"
                    prefetch={false}
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-6 py-3 text-sm font-medium text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600"
                  >
                    Sign up free
                  </Link>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════ TEMPLATE CARD ═══════ */
function TemplateCardComponent({
  template,
  featured = false,
  index = 0,
}: {
  template: TemplateCard;
  featured?: boolean;
  index?: number;
}) {
  const categoryInfo = TEMPLATE_CATEGORIES.find((c) => c.value === template.category);

  return (
    <Link
      href={`/templates/${template.slug}`}
      prefetch={false}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] dark:border-gray-800/80 dark:bg-gray-900 dark:hover:shadow-[0_20px_60px_-15px_rgba(139,92,246,0.06)]"
    >
      {/* Top gradient accent */}
      <div
        className={`h-1 w-full ${
          featured
            ? "bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500"
            : "bg-gradient-to-r from-violet-400 via-blue-400 to-violet-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        }`}
      />

      <div className="relative flex flex-1 flex-col p-5">
        {/* Featured badge */}
        {template.isFeatured && (
          <div className="absolute right-4 top-4">
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm shadow-orange-500/20">
              <Star className="h-2.5 w-2.5" fill="currentColor" />
              Featured
            </span>
          </div>
        )}

        {/* Category icon + Name */}
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-50 text-lg transition-transform duration-200 group-hover:scale-110 dark:bg-gray-800">
            {categoryInfo?.icon || "🤖"}
          </div>
          <div className="min-w-0">
            <h3 className="line-clamp-1 text-[15px] font-semibold text-gray-900 transition-colors group-hover:text-violet-600 dark:text-white dark:group-hover:text-violet-400">
              {template.name}
            </h3>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {template.author.name || "Anonymous"}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="mb-4 line-clamp-2 flex-1 text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">
          {template.shortDescription || "No description provided"}
        </p>

        {/* Tags */}
        {template.tags.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {template.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              >
                {tag}
              </span>
            ))}
            {template.tags.length > 3 && (
              <span className="text-[11px] text-gray-400">+{template.tags.length - 3}</span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 pt-3.5 dark:border-gray-800">
          <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
            <span className="flex items-center gap-1" title="Installs">
              <Download className="h-3 w-3" />
              {template.installCount}
            </span>
            <span className="flex items-center gap-1" title="Views">
              <Eye className="h-3 w-3" />
              {template.viewCount}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {template.provider && (
              <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                {template.provider}
              </span>
            )}
            <ArrowUpRight className="h-3.5 w-3.5 text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-violet-500 dark:text-gray-600" />
          </div>
        </div>
      </div>
    </Link>
  );
}
